import { DateTime } from "luxon";
import { alpha, darken, getLuminance } from "@mui/material/styles";
import { toPositionedItem, weekDatesFrom } from "/imports/ui/components/scheduleGrid/gridHelpers";

// Day mode: one column per roster entity. A booking lands on its court's
// column, or — grouped by trainer — on the column of every trainer running it
// (lead + co-trainers), the same duplication partners does client-side. Since
// each copy lives in a different column, the booking id stays unique per
// column and no synthetic keys are needed.
export function groupByEntity({ bookings, entityType, roster }) {
  const byColumn = Object.fromEntries(roster.map((r) => [r._id, []]));
  for (const b of bookings) {
    const item = toPositionedItem(b);
    if (!item) continue;
    if (entityType === "facility") {
      if (byColumn[b.facilityId]) byColumn[b.facilityId].push(item);
    } else {
      const trainerIds = [...new Set([b.trainerId, ...(b.otherTrainerIds || [])].filter(Boolean))];
      for (const id of trainerIds) {
        if (byColumn[id]) byColumn[id].push(item);
      }
    }
  }
  return byColumn;
}

// Week mode: one column per date of the Mon–Sun week.
export function groupByDate({ bookings, weekStart }) {
  const dates = weekDatesFrom(weekStart);
  const byColumn = Object.fromEntries(dates.map((d) => [d, []]));
  for (const b of bookings) {
    const item = toPositionedItem(b);
    if (item && byColumn[b.date]) byColumn[b.date].push(item);
  }
  return { dates, byColumn };
}

export function dayColumns(roster) {
  return roster.map((r) => ({
    key: r._id,
    mainLabel: r.name,
    avatar: { initials: r.initials, backgroundColor: r.backgroundColor }
  }));
}

// Mode-transition rules, mirroring partners getAdaptedFilters:
// - Week needs a specific entity: switching to week on "all" auto-picks the
//   first roster entity (and stays on day if the roster is empty).
// - "All" is day-only: picking it forces day.
// - Changing the entity axis or location resets to all + day.
export function adaptFilters(prev, patch, roster = []) {
  const next = { ...prev, ...patch };
  if (patch.entityType !== undefined || patch.locationId !== undefined) {
    next.entityId = "all";
    next.view = "day";
  }
  if (patch.entityId === "all") next.view = "day";
  if (next.view === "week" && next.entityId === "all") {
    if (roster.length) next.entityId = roster[0]._id;
    else next.view = "day";
  }
  return next;
}

// Shades of a club service color for the grid block and the booking sheet.
// Pastels get a strong tint with darkened text; dark colors (a black category)
// get a light tint so the text on top stays readable.
export function servicePalette(color) {
  const isDark = getLuminance(color) < 0.3;
  return {
    fill: alpha(color, isDark ? 0.2 : 0.45),
    hoverFill: alpha(color, isDark ? 0.32 : 0.6),
    softFill: alpha(color, isDark ? 0.1 : 0.22),
    edge: darken(color, 0.35),
    ink: darken(color, isDark ? 0.1 : 0.6)
  };
}

export function dayLabel(ymd) {
  if (!ymd) return "";
  return DateTime.fromISO(ymd, { zone: "UTC" }).toFormat("ccc, d LLL");
}

export function weekLabel(weekStart, weekEnd) {
  if (!weekStart || !weekEnd) return "";
  return `${DateTime.fromISO(weekStart, { zone: "UTC" }).toFormat("d LLL")} – ${DateTime.fromISO(weekEnd, { zone: "UTC" }).toFormat("d LLL")}`;
}
